import React, { useState, useMemo, useCallback, useRef, ChangeEvent } from "react";
import {
  Box,
  InputLabel,
  TextField,
  FormControl,
  FormHelperText,
  Button,
  Card,
  CardContent,
  Autocomplete,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Controller, useFormContext } from "react-hook-form";
import moment from "moment";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import CameraSmallIcon from "../../../shared/assets/icons/CameraSmallIcon";
import { ServiceRequestsState, ServiceRequestFormData } from "../models/models";
import { Machine } from "../../machinery/models/models";
import { ServiceReportFile, ServiceReportsState, MachinePartGroup } from "../../machinery/models/ServiceReportsModel";
import useTranslation from "../../../shared/hooks/useTranslation";
import MachineCard from "./MachineCard";
import MachineChoiceDialog from "./MachineChoiceDialog";
import AttachReportDialog from "./AttachReportDialog";
import IconDatePicker from "./shared/IconDatePicker";
import { dt } from "../../../shared/themes/redesignTokens";
import LocationPicker from "./locations/LocationPicker";
import type { Location } from "./locations/locationTypes";

// ═══════════════════════════════════════════════════
//  GROUPED MACHINE PARTS (CNC-standard)
// ═══════════════════════════════════════════════════

// Each entry stores i18n keys + the English name used for backend category matching
const MACHINE_PART_GROUPS = [
  { groupKey: "spindleSystem", parts: [
    { key: "mainSpindle", en: "Main spindle" }, { key: "spindleMotor", en: "Spindle motor" },
    { key: "spindleBearings", en: "Spindle bearings" }, { key: "toolTaperDrawbar", en: "Tool taper / drawbar" },
  ] },
  { groupKey: "axisDrives", parts: [
    { key: "servoMotors", en: "Servo motors" }, { key: "ballScrews", en: "Ball screws" },
    { key: "linearGuidesRails", en: "Linear guides / rails" }, { key: "axisBearings", en: "Axis bearings" },
  ] },
  { groupKey: "cncControl", parts: [
    { key: "controllerHmi", en: "Controller / HMI" }, { key: "ioBoards", en: "I/O boards" },
    { key: "plcModule", en: "PLC module" }, { key: "softwareFirmware", en: "Software / firmware" },
  ] },
  { groupKey: "electrical", parts: [
    { key: "powerSupply", en: "Power supply" }, { key: "cablesWiring", en: "Cables / wiring" },
    { key: "sensorsSwitches", en: "Sensors / switches" }, { key: "contactorsRelays", en: "Contactors / relays" },
  ] },
  { groupKey: "hydraulicSystem", parts: [
    { key: "hydraulicPump", en: "Hydraulic pump" }, { key: "cylinders", en: "Cylinders" },
    { key: "valves", en: "Valves" }, { key: "hosesFittings", en: "Hoses / fittings" },
  ] },
  { groupKey: "pneumaticSystem", parts: [
    { key: "airSystem", en: "Air system" }, { key: "pneumaticValves", en: "Pneumatic valves" },
    { key: "pneumaticCylinders", en: "Pneumatic cylinders" },
  ] },
  { groupKey: "coolantSystem", parts: [
    { key: "coolantPump", en: "Coolant pump" }, { key: "nozzles", en: "Nozzles" },
    { key: "tankFiltration", en: "Tank / filtration" },
  ] },
  { groupKey: "lubrication", parts: [
    { key: "lubricationPump", en: "Lubrication pump" }, { key: "distributionLines", en: "Distribution lines" },
  ] },
  { groupKey: "toolChanger", parts: [
    { key: "atcMechanism", en: "ATC mechanism" }, { key: "toolMagazine", en: "Tool magazine" },
    { key: "gripperArm", en: "Gripper arm" },
  ] },
  { groupKey: "turretLathes", parts: [
    { key: "turretMechanism", en: "Turret mechanism" }, { key: "toolHolders", en: "Tool holders" },
    { key: "indexingMotor", en: "Indexing motor" },
  ] },
  { groupKey: "workholding", parts: [
    { key: "chuckCollet", en: "Chuck / collet" }, { key: "jaws", en: "Jaws" },
    { key: "tailstock", en: "Tailstock" }, { key: "steadyRest", en: "Steady rest" },
  ] },
  { groupKey: "chipManagement", parts: [
    { key: "chipConveyor", en: "Chip conveyor" }, { key: "chipAuger", en: "Chip auger" },
  ] },
  { groupKey: "enclosureSafety", parts: [
    { key: "doorsGuards", en: "Doors / guards" }, { key: "interlocks", en: "Interlocks" },
    { key: "wayCovers", en: "Way covers" },
  ] },
  { groupKey: "consumables", parts: [
    { key: "filters", en: "Filters" }, { key: "belts", en: "Belts" },
    { key: "sealsORings", en: "Seals / O-rings" }, { key: "wipers", en: "Wipers" },
  ] },
  { groupKey: "other", parts: [
    { key: "other", en: "Other" },
  ] },
];

interface PartOption {
  label: string;
  group: string;
  englishName: string;
  categoryId?: string;
}

// ═══════════════════════════════════════════════════
//  FILE ICON HELPER
// ═══════════════════════════════════════════════════

const getFileIcon = (name: string) => {
  const ext = name.toLowerCase().split(".").pop();
  if (ext === "pdf") return <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: "#E74C3C" }} />;
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext || ""))
    return <ImageOutlinedIcon sx={{ fontSize: 18, color: dt.blue }} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_FILE_SIZE_MB = 10;

// ═══════════════════════════════════════════════════
//  PROPS
// ═══════════════════════════════════════════════════

export interface StepMachineAndDetailsProps {
  selectedMachine?: Machine;
  files?: ServiceReportFile[];
  onSelectedMachineChange?: (machine?: Machine) => void;
  onSelectedReportNumberChange?: (reportNumber: string) => void;
  onFilesChange?: (files: ServiceReportFile[]) => void;
  locations?: Location[];
  selectedLocationId?: string | null;
  onLocationChange?: (locationId: string | null) => void;
}

// ═══════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════

const StepMachineAndDetails = ({
  selectedMachine,
  files,
  onSelectedMachineChange,
  onSelectedReportNumberChange,
  onFilesChange,
  locations = [],
  selectedLocationId,
  onLocationChange,
}: StepMachineAndDetailsProps) => {
  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);
  const { serviceRequest } = useAppSelector<ServiceRequestsState>(
    (state: any) => state.customerServicing
  );
  const { machineParts } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );

  const serviceRequestNumber = serviceRequest?.internalNumber || undefined;
  const isNew: boolean = !serviceRequestNumber;
  const isPhone = useMediaQuery("(max-width:768px)");

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMachineChoiceDialogOpen, setIsMachineChoiceDialogOpen] = useState(false);
  const [isAttachReportDialogOpen, setIsAttachReportDialogOpen] = useState(false);

  // File upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string>();

  const {
    register,
    watch,
    control,
    trigger,
    formState: { errors },
  } = useFormContext<ServiceRequestFormData>();

  const openDate = watch("openDate");

  const openMachineDialog = () => {
    setIsMachineChoiceDialogOpen(true);
  };

  // ── Build grouped part options from Machinery API ──────────────────

  const partOptions: PartOption[] = useMemo(() => {
    const opts: PartOption[] = [];
    if (machineParts && machineParts.length > 0) {
      for (const group of machineParts) {
        const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
        for (const part of group.parts) {
          // Use code as translation key (e.g., main_spindle -> mainSpindle)
          const translationKey = part.code.replace(/_([a-z])/g, (_m: string, c: string) => c.toUpperCase());
          const partLabel = translate(`servicing:machineParts.parts.${translationKey}`);
          opts.push({ label: partLabel, group: groupLabel, englishName: part.name, categoryId: part.id });
        }
      }
    } else {
      // Fallback to hardcoded groups if API hasn't loaded yet
      for (const group of MACHINE_PART_GROUPS) {
        const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
        for (const part of group.parts) {
          const partLabel = translate(`servicing:machineParts.parts.${part.key}`);
          opts.push({ label: partLabel, group: groupLabel, englishName: part.en });
        }
      }
    }
    return opts;
  }, [machineParts, translate]);

  // ── File upload handlers ──────────────────────

  const readFileAsDataUrl = useCallback(
    (file: File): Promise<ServiceReportFile | null> => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(translate("common:uploadFile.fileIsTooBig"));
        return Promise.resolve(null);
      }
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({
            name: file.name,
            content: reader.result as string,
            size: file.size,
          });
        };
        reader.onerror = () => resolve(null);
      });
    },
    [translate]
  );

  const processFiles = useCallback(
    async (fileList: File[]) => {
      setFileError(undefined);
      const results = await Promise.all(fileList.map((f) => readFileAsDataUrl(f)));
      const validFiles = results.filter((f): f is ServiceReportFile => f !== null);
      if (validFiles.length > 0) {
        const currentFiles = files || [];
        onFilesChange?.([...currentFiles, ...validFiles]);
      }
    },
    [files, onFilesChange, readFileAsDataUrl]
  );

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        processFiles(Array.from(e.dataTransfer.files));
      }
    },
    [processFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(Array.from(e.target.files));
      }
      e.target.value = "";
    },
    [processFiles]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      const updated = (files || []).filter((_, i) => i !== index);
      onFilesChange?.(updated);
    },
    [files, onFilesChange]
  );

  // ── Styles ──────────────────────────────────────

  const sectionSx = {
    backgroundColor: dt.cardBg,
    borderRadius: dt.radius,
    border: `1px solid ${dt.cardBorder}`,
    boxShadow: dt.cardShadow,
    p: { xs: 2.5, md: 4 },
    display: "flex",
    flexDirection: "column" as const,
    gap: 3,
  };

  const labelSx = {
    fontFamily: dt.font,
    fontSize: "13px",
    fontWeight: 600,
    color: dt.textPrimary,
    letterSpacing: "0.1px",
    mb: 0.5,
  };

  const showForm = !!selectedMachine;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isPhone ? "column" : "row",
        gap: 3,
        animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) both",
      }}
    >
      {/* ═══ Left column -- Machine selector ═══ */}
      <Box sx={{ width: isPhone ? "100%" : "300px", flexShrink: 0 }}>
        {selectedMachine ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              animation: "fadeLeft 0.4s cubic-bezier(0.4,0,0.2,1) both",
            }}
          >
            <MachineCard machine={selectedMachine} />

            <Button
              fullWidth
              variant="outlined"
              onClick={openMachineDialog}
              sx={{
                fontFamily: dt.font,
                fontWeight: 600,
                fontSize: "13px",
                textTransform: "none",
                borderRadius: dt.radiusSm,
                borderColor: dt.cardBorder,
                color: dt.textSecondary,
                "&:hover": {
                  borderColor: dt.blue,
                  color: dt.blue,
                  backgroundColor: dt.blueSoft,
                },
              }}
            >
              {translate("myMachinery:changeMachine")}
            </Button>

            {/* Location Picker */}
            {locations.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: dt.textPrimary,
                    letterSpacing: "0.1px",
                    mb: 0.5,
                  }}
                >
                  {translate("servicing:location") || "Location"}
                </Box>
                <LocationPicker
                  value={selectedLocationId ?? null}
                  onChange={(id) => onLocationChange?.(id)}
                  locations={locations}
                  placeholder={translate("servicing:selectLocation") || "Select location..."}
                  size="small"
                />
              </Box>
            )}
          </Box>
        ) : (
          <Card
            sx={{
              borderRadius: dt.radius,
              border: `1px solid ${dt.cardBorder}`,
              boxShadow: dt.cardShadow,
              animation: "fadeLeft 0.4s cubic-bezier(0.4,0,0.2,1) both",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100px",
                  height: "100px",
                  backgroundColor: dt.inputBg,
                  borderRadius: dt.radiusSm,
                  mt: 4,
                }}
              >
                <CameraSmallIcon
                  sx={{
                    color: dt.textMuted,
                    width: "48px",
                    height: "39px",
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pb: 2,
                }}
              >
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "16px",
                    fontWeight: 700,
                    color: dt.textPrimary,
                  }}
                >
                  {translate("servicing:machineForRequest")}
                </Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: dt.textSecondary,
                  }}
                >
                  {translate("servicing:yourProductionMachine")}
                </Box>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={openMachineDialog}
                sx={{
                  fontFamily: dt.font,
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  borderRadius: dt.radiusPill,
                  background: `linear-gradient(135deg, ${dt.blue} 0%, #2E86C1 100%)`,
                  boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                  "&:hover": {
                    background: `linear-gradient(135deg, #2E86C1 0%, ${dt.blue} 100%)`,
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 14px rgba(52,152,219,0.3)",
                  },
                  transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                {translate("myMachinery:choose")}
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* ═══ Right column -- Service Details ═══ */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!showForm ? (
          /* ── Empty state: select a machine first ── */
          <Box
            sx={{
              ...sectionSx,
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px",
              gap: 2,
            }}
          >
            <PrecisionManufacturingOutlinedIcon
              sx={{ fontSize: 56, color: dt.textMuted, opacity: 0.4 }}
            />
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "16px",
                fontWeight: 600,
                color: dt.textSecondary,
              }}
            >
              {translate("servicing:selectMachineFirst") || "Select a machine to get started"}
            </Typography>
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                color: dt.textMuted,
                textAlign: "center",
              }}
            >
              {translate("servicing:selectMachineHint") || "Choose the machine that needs servicing"}
            </Typography>
          </Box>
        ) : (
          /* ── Form content ── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              animation: "fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) both",
            }}
          >
            {/* ── Flow option cards ── */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isPhone ? "column" : "row",
                gap: 2,
              }}
            >
              {/* Load from Service Report */}
              <Box
                onClick={() => setIsAttachReportDialogOpen(true)}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: dt.radiusSm,
                  border: `1.5px solid ${dt.cardBorder}`,
                  backgroundColor: dt.cardBg,
                  cursor: "pointer",
                  transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                  borderLeft: `4px solid ${dt.blue}`,
                  "&:hover": {
                    borderColor: dt.blue,
                    boxShadow: dt.cardShadowHover,
                    transform: "translateY(-2px)",
                    backgroundColor: dt.blueSoft,
                  },
                  animation: "fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) 0.05s both",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: dt.blueSoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ color: dt.blue, fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: dt.textPrimary,
                    }}
                  >
                    {translate("servicing:flowOptionLoadReport") || "Load from Service Report"}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: dt.textSecondary,
                    }}
                  >
                    {translate("servicing:flowOptionLoadReportHint") || "Pre-fill details from an existing report"}
                  </Typography>
                </Box>
              </Box>

              {/* Create Manually */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: dt.radiusSm,
                  border: `1.5px solid ${dt.blue}`,
                  backgroundColor: dt.blueSoft,
                  borderLeft: `4px solid ${dt.blue}`,
                  animation: "fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) 0.1s both",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${dt.blue} 0%, #2E86C1 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <EditNoteOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: dt.blue,
                    }}
                  >
                    {translate("servicing:flowOptionManual") || "Create Manually"}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: dt.textSecondary,
                    }}
                  >
                    {translate("servicing:flowOptionManualHint") || "Start with a blank form"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Service details form card ── */}
            <Box sx={sectionSx}>
              {/* Header */}
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: { xs: "16px", md: "18px" },
                  fontWeight: 700,
                  color: dt.textPrimary,
                }}
              >
                {translate("myMachinery:serviceDetails")}
              </Box>

              {/* Title (renamed from Issue) */}
              <Box>
                <InputLabel required sx={labelSx}>
                  {translate("servicing:rfsTitle") || translate("myMachinery:issue")}
                </InputLabel>
                <TextField
                  fullWidth
                  {...register("issue", {
                    required: translate("common:forms.required"),
                    maxLength: {
                      value: 100,
                      message: translate("myMachinery:titleTooLong"),
                    },
                  })}
                  error={!!errors.issue}
                  helperText={
                    errors.issue?.message ||
                    (translate("servicing:rfsTitleHint") || "This will be the first thing service providers see")
                  }
                  FormHelperTextProps={{
                    sx: {
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: errors.issue ? undefined : dt.textMuted,
                      fontStyle: errors.issue ? undefined : "italic",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: dt.radiusSm,
                      "&.Mui-focused fieldset": {
                        borderColor: dt.blue,
                      },
                    },
                  }}
                />
              </Box>

              {/* Open Date + Time */}
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <Box>
                    <InputLabel sx={labelSx}>
                      {translate("myMachinery:openDate")}
                    </InputLabel>
                    <Controller
                      control={control}
                      name="openDate"
                      rules={{
                        validate: (value) => {
                          if (value) {
                            if (moment(value) > moment()) {
                              return translate("myMachinery:openDateFuture");
                            }
                          }
                        },
                      }}
                      render={({ field }) => (
                        <IconDatePicker
                          field={field}
                          name="openDate"
                          disableFuture
                          maxDate={moment()}
                          isOpen={isCalendarOpen}
                          onOpen={() => setIsCalendarOpen(true)}
                          onClose={() => setIsCalendarOpen(false)}
                        />
                      )}
                    />
                  </Box>

                  <Box>
                    <InputLabel sx={labelSx}>
                      {translate("common:common.time")}
                    </InputLabel>
                    <FormControl fullWidth>
                      <Controller
                        control={control}
                        name="openDate"
                        render={({ field }) => (
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <TimeField
                              {...field}
                              value={openDate ? moment(openDate) : null}
                              onChange={(value) => {
                                if (value) {
                                  const currentDate = openDate
                                    ? moment(openDate)
                                    : moment();
                                  const updatedDate = currentDate
                                    .hour(value.hour())
                                    .minute(value.minute())
                                    .second(0)
                                    .millisecond(0);
                                  field.onChange(updatedDate.toDate());
                                }
                              }}
                              format="HH:mm"
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </FormControl>
                  </Box>
                </Box>

                <Box sx={{ mt: 0.5 }}>
                  <FormHelperText error>
                    {errors.openDate?.message}
                  </FormHelperText>
                </Box>
              </Box>

              {/* Affected Machine Part -- Grouped Autocomplete */}
              <Box>
                <InputLabel required sx={labelSx}>
                  {translate("myMachinery:affectedMachinePart")}
                </InputLabel>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="affectedPartCategoryId"
                    rules={{ required: translate("common:forms.required") }}
                    render={({ field }) => {
                      // Find current selection
                      const selectedOption = partOptions.find(
                        (o) => o.categoryId === field.value
                      ) || null;

                      return (
                        <Autocomplete
                          options={partOptions.filter((o) => !!o.categoryId)}
                          groupBy={(option) => option.group}
                          getOptionLabel={(option) => option.label}
                          value={selectedOption}
                          onChange={(_e, newValue) => {
                            field.onChange(newValue?.categoryId || "");
                            setTimeout(() => trigger("affectedPartCategoryId"));
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.categoryId === value.categoryId
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!errors.affectedPartCategoryId}
                              placeholder={translate("myMachinery:affectedMachinePart")}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: dt.radiusSm,
                                  "&.Mui-focused fieldset": {
                                    borderColor: dt.blue,
                                  },
                                },
                              }}
                            />
                          )}
                          renderGroup={(params) => (
                            <Box key={params.key}>
                              <Box
                                sx={{
                                  fontFamily: dt.font,
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.8px",
                                  color: dt.textSecondary,
                                  px: 2,
                                  pt: 1.5,
                                  pb: 0.5,
                                  position: "sticky",
                                  top: 0,
                                  backgroundColor: "#fff",
                                  zIndex: 1,
                                }}
                              >
                                {params.group}
                              </Box>
                              {params.children}
                            </Box>
                          )}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              {...props}
                              sx={{
                                fontFamily: `${dt.font} !important`,
                                fontSize: "14px !important",
                                pl: "32px !important",
                                py: "6px !important",
                                "&:hover": {
                                  backgroundColor: `${dt.blueSoft} !important`,
                                },
                                "&.Mui-focused": {
                                  backgroundColor: `${dt.blueSoft} !important`,
                                },
                              }}
                            >
                              {option.label}
                            </Box>
                          )}
                          slotProps={{
                            paper: {
                              sx: {
                                borderRadius: dt.radiusSm,
                                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                maxHeight: 350,
                              },
                            },
                          }}
                        />
                      );
                    }}
                  />
                  <FormHelperText error>
                    {errors.affectedPartCategoryId?.message}
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Alarm Code */}
              <Box>
                <InputLabel sx={labelSx}>
                  {translate("servicing:alarmCode")}
                </InputLabel>
                <TextField
                  fullWidth
                  {...register("alarmCode", {
                    maxLength: {
                      value: 100,
                      message: translate("servicing:alarmCodeTooLong") || "Alarm code is too long (max 100 characters)",
                    },
                  })}
                  error={!!errors.alarmCode}
                  helperText={
                    errors.alarmCode?.message ||
                    (translate("servicing:alarmCodeHint") || "Machine alarm or error code (if applicable)")
                  }
                  FormHelperTextProps={{
                    sx: {
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: errors.alarmCode ? undefined : dt.textMuted,
                      fontStyle: errors.alarmCode ? undefined : "italic",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: dt.radiusSm,
                      "&.Mui-focused fieldset": {
                        borderColor: dt.blue,
                      },
                    },
                  }}
                />
              </Box>

              {/* Description */}
              <Box>
                <InputLabel sx={labelSx}>
                  {translate("common:description")}
                </InputLabel>
                <Box
                  sx={{
                    background: dt.inputBg,
                    borderRadius: dt.radiusSm,
                    border: "1.5px solid transparent",
                    padding: "12px",
                    transition: "all 0.2s ease",
                    "&:focus-within": {
                      border: `1.5px solid ${dt.blue}`,
                      boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                    },
                  }}
                >
                  <textarea
                    {...register("description")}
                    placeholder={translate("myMachinery:descriptionPlaceholder")}
                    maxLength={2000}
                    rows={6}
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      fontSize: "14px",
                      fontFamily: "var(--font-sans)",
                      color: "#1a1a2e",
                      background: "transparent",
                      resize: "vertical",
                      minHeight: "120px",
                      boxSizing: "border-box",
                    }}
                  />
                </Box>
                {errors.description && (
                  <FormHelperText error>
                    {errors.description?.message}
                  </FormHelperText>
                )}
              </Box>

              {/* Notes */}
              <Box>
                <InputLabel sx={labelSx}>
                  {translate("servicing:notes")}
                </InputLabel>
                <Box
                  sx={{
                    background: dt.inputBg,
                    borderRadius: dt.radiusSm,
                    border: "1.5px solid transparent",
                    padding: "12px",
                    transition: "all 0.2s ease",
                    "&:focus-within": {
                      border: `1.5px solid ${dt.blue}`,
                      boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                    },
                  }}
                >
                  <textarea
                    {...register("notes")}
                    placeholder={translate("servicing:notesPlaceholder") || "Additional notes or observations..."}
                    maxLength={4000}
                    rows={4}
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      fontSize: "14px",
                      fontFamily: "var(--font-sans)",
                      color: "#1a1a2e",
                      background: "transparent",
                      resize: "vertical",
                      minHeight: "80px",
                      boxSizing: "border-box",
                    }}
                  />
                </Box>
                {errors.notes && (
                  <FormHelperText error>
                    {errors.notes?.message}
                  </FormHelperText>
                )}
              </Box>

              {/* ── Elevated File Upload Zone ── */}
              <Box>
                <InputLabel sx={labelSx}>
                  {translate("servicing:attachments")}
                </InputLabel>

                {/* Drop zone */}
                <Box
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(false);
                  }}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    height: "100px",
                    border: `2px dashed ${
                      fileError ? "#C0392B" : isDragOver ? dt.blue : dt.cardBorder
                    }`,
                    borderRadius: dt.radiusSm,
                    backgroundColor: isDragOver ? dt.blueGlow : dt.inputBg,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: dt.blue,
                      backgroundColor: dt.blueSoft,
                    },
                  }}
                >
                  <CloudUploadOutlinedIcon
                    sx={{
                      fontSize: 28,
                      color: isDragOver ? dt.blue : dt.textMuted,
                      transition: "color 0.2s ease",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: dt.textSecondary,
                      fontFamily: dt.font,
                    }}
                  >
                    {translate("common:uploadFile.dragAndDrop")}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "11px",
                      color: dt.textMuted,
                      fontFamily: dt.font,
                    }}
                  >
                    {translate("common:uploadFile.supportedFormats")}
                  </Box>
                </Box>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileInputChange}
                />

                {/* Error */}
                {fileError && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {fileError}
                  </FormHelperText>
                )}

                {/* File list */}
                {(files || []).length > 0 && (
                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                    }}
                  >
                    {(files || []).map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1,
                          borderRadius: dt.radiusSm,
                          border: `1px solid ${dt.cardBorder}`,
                          backgroundColor: dt.cardBg,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#FAFAFA",
                            "& .file-delete": {
                              opacity: 1,
                            },
                          },
                          animation: `fadeUp 0.3s ease ${index * 0.05}s both`,
                        }}
                      >
                        {getFileIcon(file.name)}
                        <Typography
                          sx={{
                            fontFamily: dt.font,
                            fontSize: "13px",
                            fontWeight: 500,
                            color: dt.textPrimary,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {file.name}
                        </Typography>
                        {file.size > 0 && (
                          <Typography
                            sx={{
                              fontFamily: dt.fontMono,
                              fontSize: "11px",
                              fontWeight: 500,
                              color: dt.textMuted,
                              flexShrink: 0,
                            }}
                          >
                            {formatFileSize(file.size)}
                          </Typography>
                        )}
                        <Tooltip title={translate("common:buttons.delete")}>
                          <IconButton
                            className="file-delete"
                            size="small"
                            onClick={() => handleRemoveFile(index)}
                            sx={{
                              opacity: 0.5,
                              transition: "opacity 0.2s ease",
                              p: "4px",
                              "&:hover": {
                                backgroundColor: "rgba(220,38,38,0.08)",
                                color: "#C0392B",
                              },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* ═══ Dialogs (unchanged logic) ═══ */}
      <MachineChoiceDialog
        open={isMachineChoiceDialogOpen}
        onClose={() => setIsMachineChoiceDialogOpen(false)}
        selectedMachineId={selectedMachine?.id}
        onChoose={(machine: Machine) => {
          onSelectedMachineChange?.(machine);
          setIsMachineChoiceDialogOpen(false);
        }}
      />

      <AttachReportDialog
        open={isAttachReportDialogOpen}
        onClose={() => setIsAttachReportDialogOpen(false)}
        selectedMachineId={selectedMachine?.id}
        machineParts={machineParts}
        onChoose={(reportNumber: string) => {
          onSelectedReportNumberChange?.(reportNumber);
          setIsAttachReportDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default StepMachineAndDetails;
